import {
  isGeneAnalysisPublicEnabled,
  isKeisukePublicEnabled,
  isLaboSukePublicEnabled,
  isTppPublicEnabled,
} from '../utils/config';

export type KensukeFeatureCardId =
  | 'keisuke'
  | 'sample'
  | 'reservation'
  | 'labo_suke'
  | 'gaisuke'
  | 'gaisuke_fx'
  | 'keiba_arena'
  | 'banquet'
  | 'econ_report'
  | 'gene_analysis'
  | 'transit_pass';

export type KensukeFeatureVisibility = 'always' | 'loggedIn' | 'admin' | 'hidden';

/** カードに出すアクセス語彙（デモ / ログイン不要 / ログイン必須） */
export type KensukeAccessBadge = 'demo' | 'public' | 'login_required';

/**
 * 面の区分。`ai` は AI が生成・運用する読み物・実験（トップとダッシュボードで
 * 「AIコンテンツ」節にまとめる）。`tool` は利用者が自分の作業に使う道具。
 */
export type KensukeFeatureGroup = 'ai' | 'tool';

export interface KensukeFeatureCardDef {
  id: KensukeFeatureCardId;
  group: KensukeFeatureGroup;
  homeOrder: number;
  dashboardOrder: number;
  homeVisibility: KensukeFeatureVisibility;
  dashboardVisibility: KensukeFeatureVisibility;
  emoji?: string;
  icon?: 'gaisuke';
  /** Left accent / icon tint — design-system blues/greens/slate (no purple). */
  accentColor: string;
  /** 常に表示するアクセスバッジ（語彙はこの3種のみ） */
  accessBadges: KensukeAccessBadge[];
  titleKey: string;
  descriptionKey: string;
  /** トップとダッシュボードの両方に出す短い状態（任意） */
  noteKey?: string;
  /** トップカード本文の下に出す短い要点（任意） */
  homeBulletKeys?: readonly string[];
}

/** 開発中機能（一般ユーザー向けトップ・ダッシュボードでは非表示） */
export function isKensukeFeatureInDevelopment(id: KensukeFeatureCardId): boolean {
  switch (id) {
    case 'gene_analysis':
      return !isGeneAnalysisPublicEnabled();
    case 'labo_suke':
      return !isLaboSukePublicEnabled();
    case 'keisuke':
      return !isKeisukePublicEnabled();
    case 'transit_pass':
      // Zip 正本・公開が既定。公開オフ時はカード文言（ログイン不要）と矛盾するため非表示
      return !isTppPublicEnabled();
    default:
      return false;
  }
}

function isVisible(
  rule: KensukeFeatureVisibility,
  opts: { isLoggedIn: boolean; isAdmin: boolean }
): boolean {
  switch (rule) {
    case 'always':
      return true;
    case 'loggedIn':
      return opts.isLoggedIn;
    case 'admin':
      return opts.isAdmin;
    case 'hidden':
      return false;
    default:
      return false;
  }
}

export function getKensukeFeatureCardsForSurface(
  surface: 'home' | 'dashboard',
  opts: {
    isLoggedIn: boolean;
    isAdmin: boolean;
    /** true のとき keiba_arena カードを除外（特定 IP 向け） */
    hideKeibaArenaFeatureCard?: boolean;
    /** 指定するとその区分のカードだけを返す */
    group?: KensukeFeatureGroup;
  }
): KensukeFeatureCardDef[] {
  return KENSUKE_FEATURE_CARD_DEFS.filter((def) => {
    if (opts.group && def.group !== opts.group) return false;
    if (opts.hideKeibaArenaFeatureCard && def.id === 'keiba_arena') return false;
    if (isKensukeFeatureInDevelopment(def.id)) return false;
    const rule = surface === 'home' ? def.homeVisibility : def.dashboardVisibility;
    return isVisible(rule, opts);
  }).sort((a, b) => {
    const orderA = surface === 'home' ? a.homeOrder : a.dashboardOrder;
    const orderB = surface === 'home' ? b.homeOrder : b.dashboardOrder;
    return orderA - orderB;
  });
}

/**
 * Home: ログイン不要を先に（すぐ試せる順）→ ログイン必須。
 * Dashboard: 研究系・管理系を先に（作業再開しやすい順）。
 */
export const KENSUKE_FEATURE_CARD_DEFS: KensukeFeatureCardDef[] = [
  {
    id: 'keisuke',
    group: 'tool',
    homeOrder: 10,
    dashboardOrder: 10,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '📊',
    accentColor: '#2563eb',
    accessBadges: ['public'],
    titleKey: 'home.feature_keisuke_title',
    descriptionKey: 'home.feature_keisuke_description',
  },
  {
    id: 'transit_pass',
    group: 'tool',
    homeOrder: 20,
    dashboardOrder: 40,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '💳',
    accentColor: '#0284c7',
    accessBadges: ['public'],
    titleKey: 'home.feature_transit_pass_title',
    descriptionKey: 'home.feature_transit_pass_description',
  },
  {
    id: 'labo_suke',
    group: 'tool',
    homeOrder: 30,
    dashboardOrder: 50,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '📒',
    accentColor: '#16a34a',
    accessBadges: ['public'],
    titleKey: 'home.feature_labo_suke_title',
    descriptionKey: 'home.feature_labo_suke_description',
  },
  {
    id: 'gaisuke',
    group: 'ai',
    homeOrder: 40,
    dashboardOrder: 60,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    icon: 'gaisuke',
    accentColor: '#0ea5e9',
    accessBadges: ['public'],
    titleKey: 'home.feature_gaisuke_title',
    descriptionKey: 'home.feature_gaisuke_description',
  },
  {
    id: 'gaisuke_fx',
    group: 'ai',
    homeOrder: 45,
    dashboardOrder: 62,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '💱',
    accentColor: '#0369a1',
    accessBadges: ['public'],
    titleKey: 'home.feature_gaisuke_fx_title',
    descriptionKey: 'home.feature_gaisuke_fx_description',
    noteKey: 'home.feature_gaisuke_fx_ea_note',
  },
  {
    id: 'keiba_arena',
    group: 'ai',
    homeOrder: 50,
    dashboardOrder: 65,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '🏇',
    accentColor: '#059669',
    accessBadges: ['public'],
    titleKey: 'home.feature_keiba_arena_title',
    descriptionKey: 'home.feature_keiba_arena_description',
  },
  {
    id: 'banquet',
    group: 'ai',
    homeOrder: 55,
    dashboardOrder: 67,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '🕯️',
    accentColor: '#b91c1c',
    accessBadges: ['public'],
    titleKey: 'home.feature_banquet_title',
    descriptionKey: 'home.feature_banquet_description',
  },
  {
    id: 'econ_report',
    group: 'ai',
    homeOrder: 57,
    dashboardOrder: 68,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '📈',
    accentColor: '#0284c7',
    accessBadges: ['public'],
    titleKey: 'home.feature_econ_report_title',
    descriptionKey: 'home.feature_econ_report_description',
  },
  {
    id: 'sample',
    group: 'tool',
    homeOrder: 60,
    dashboardOrder: 20,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '🧪',
    accentColor: '#475569',
    /** Zip＝ログイン不要 / クラウド DB＝ログイン必須の両方が正 */
    accessBadges: ['public', 'login_required'],
    titleKey: 'home.feature_sample_title',
    descriptionKey: 'home.feature_sample_description',
  },
  {
    id: 'reservation',
    group: 'tool',
    homeOrder: 70,
    dashboardOrder: 30,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '📅',
    accentColor: '#16a34a',
    accessBadges: ['login_required'],
    titleKey: 'home.feature_reservation_title',
    descriptionKey: 'home.feature_reservation_description',
  },
  {
    id: 'gene_analysis',
    group: 'tool',
    homeOrder: 25,
    dashboardOrder: 15,
    homeVisibility: 'always',
    dashboardVisibility: 'loggedIn',
    emoji: '🧬',
    accentColor: '#0891b2',
    accessBadges: ['public'],
    titleKey: 'home.feature_gene_title',
    descriptionKey: 'home.feature_gene_description',
  },
];
